import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import SceneShell from "../components/SceneShell";
import VideoPosterTile from "../components/VideoPosterTile";
import VideoLightbox from "../components/VideoLightbox";

/**
 * Scene 15 — Product demos · landing slide.
 *
 *   • Anchor headline + a "PRODUCT DEMOS" pill
 *   • Two plain video poster tiles (PresenTour · NarraTour)
 *   • Click a tile → opens the video in a portrait lightbox
 *
 * Streamable handles the actual playback (signed URLs auto-refresh).
 */

const GREEN = "#96C11F";
const AQUA = "#00deb6";
const GREEN_GRADIENT = "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)";

const TILES = [
  {
    id: "of6vgc",
    label: "PresenTour",
    caption: "Avatar-led property tour",
    accent: GREEN,
  },
  {
    id: "rzestu",
    label: "NarraTour",
    caption: "Narrated walkthrough",
    accent: AQUA,
  },
];

export default function Scene15_ProductDemos() {
  const [openId, setOpenId] = useState(null);
  const openTile = TILES.find((t) => t.id === openId);

  return (
    <SceneShell variant="forest" align="center" data-testid="scene-product-demos">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(150,193,31,0.14) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase"
            style={{
              background: "rgba(150,193,31,0.16)",
              border: "1px solid rgba(150,193,31,0.45)",
              color: GREEN,
            }}
          >
            <Sparkles className="w-3 h-3" />
            Product demos
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-center text-3xl sm:text-4xl lg:text-5xl text-white max-w-4xl mx-auto"
          style={{ lineHeight: 1.18 }}
          data-testid="demos-title"
        >
          "Consistent, on-brand scripts…
          <br />
          <span
            style={{
              background: GREEN_GRADIENT,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            in seconds.
          </span>
          "
        </motion.h2>

        {/* Two plain poster tiles · portrait */}
        <div className="mt-10 grid grid-cols-2 gap-6 max-w-[640px] mx-auto">
          {TILES.map((tile, i) => (
            <VideoPosterTile
              key={tile.id}
              label={tile.label}
              caption={tile.caption}
              accentColor={tile.accent}
              posterUrl={`/video-posters/${tile.id}.jpg`}
              onClick={() => setOpenId(tile.id)}
              testId={`demo-tile-${i}`}
              delay={0.6 + i * 0.18}
            />
          ))}
        </div>
      </div>

      <VideoLightbox
        open={!!openTile}
        onClose={() => setOpenId(null)}
        streamableId={openTile?.id}
        label={openTile?.label}
      />
    </SceneShell>
  );
}
